import React, { useEffect, useRef } from 'react'
import { Stack, Box, Typography, Button, CircularProgress } from '@mui/material'
import { UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query'
import { ListSkeleton } from '@/components/ListSkeleton'
import { PaginatedResponse } from '@/types/Common'

interface InfiniteScrollListProps<T, E = unknown> {
  query: UseInfiniteQueryResult<InfiniteData<PaginatedResponse<T>>, E>
  renderItem: (item: T) => React.ReactNode
  emptyMessage: string
  emptyTitle?: string
  loadingCount?: number
  spacing?: number
  getKey?: (item: T) => React.Key
  skeleton?: React.ReactNode
}

export function InfiniteScrollList<T, E = unknown>({
  query,
  renderItem,
  emptyMessage,
  emptyTitle = 'Nenhum resultado encontrado.',
  loadingCount = 3,
  spacing = 1.5,
  getKey,
  skeleton,
}: InfiniteScrollListProps<T, E>) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    refetch,
  } = query

  const observerTarget = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '200px'
      }
    )

    const currentTarget = observerTarget.current
    if (currentTarget) {
      observer.observe(currentTarget)
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget)
      }
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const items = React.useMemo(() => {
    return data?.pages.flatMap((page: PaginatedResponse<T>) => page.results) || []
  }, [data])

  if (status === 'pending') {
    return (
      <ListSkeleton.Wrapper count={loadingCount}>
        {skeleton || <ListSkeleton.Rectangle />}
      </ListSkeleton.Wrapper>
    )
  }

  if (status === 'error') {
    return (
      <Stack spacing={2} marginY={5} width="100%" textAlign="center" alignItems="center">
        <Typography variant="h6" color="error">
          Erro ao carregar dados
        </Typography>
        <Button onClick={() => refetch()} variant="outlined">
          Tentar novamente
        </Button>
      </Stack>
    )
  }

  if (items.length === 0) {
    return (
      <Stack spacing={2} marginY={5} width="100%" textAlign="center" alignItems="center">
        <Box component="img" src="/assets/png/empty.png" width="100%" maxWidth={200} />
        <Typography variant="h6" color="text.primary">
          {emptyTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack spacing={spacing} width="100%">
      {items.map((item: T, index: number) => (
        <React.Fragment key={getKey ? getKey(item) : index}>
          {renderItem(item)}
        </React.Fragment>
      ))}

      <Box ref={observerTarget} textAlign="center" py={2} width="100%" minHeight={1}>
        {isFetchingNextPage && <CircularProgress size={24} />}
      </Box>
    </Stack>
  )
}
